import PhoneIcon from '@mui/icons-material/Phone'
import { Autocomplete, Box, Button, Chip, Divider, Grid2 as Grid, InputAdornment, Stack, TextField, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import React, { useCallback, useRef } from 'react'
import { IoLogoWechat } from 'react-icons/io5'

import FamilyMemberForm from './FamilyMemberForm'
import TiptapEditor from './TiptapEditor'
import { CERTIFICATIONS } from '../../share/Constant'
import { SPORTS_OPTIONS } from '../../utils/UIConstant'
import { isValidNorthAmericanPhoneNumber, isValidWeChatId } from '../../utils/utility'

const ContactInfoNoticeInline = ({ value }) => {
  const phone = (value?.phoneNumber || '').trim()
  const wechat = (value?.weChatId || '').trim()
  const noContact = !phone && !wechat

  return (
    <Stack spacing={1} sx={{ mb: 1 }}>
      {noContact && (
        <Typography color="warning.main">Please provide at least one contact method (WeChat ID or phone number) so your instructor can reach you when needed.</Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        Your contact information is only visible to your instructor and helps them contact you for class updates and coordination.
      </Typography>
    </Stack>
  )
}

export default function UserProfileForm({ value, onInputChange, onEmergencyContactChange, onFamilyMembersChange }) {
  // 使用 useCallback 稳定 onChange 函数，避免 TiptapEditor 不必要的重新渲染
  const handleBioChange = useCallback(
    (newBio) => {
      onInputChange('bio', newBio)
    },
    [onInputChange],
  )

  const bioRef = useRef(value?.bio || '')

  return (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField fullWidth label="First Name" value={value?.firstName || ''} onChange={(e) => onInputChange('firstName', e.target.value)} size="small" autoFocus />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField fullWidth label="Last Name" value={value?.lastName || ''} onChange={(e) => onInputChange('lastName', e.target.value)} size="small" />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            About Me
          </Typography>
        </Box>
        <TiptapEditor defaultContent={bioRef.current} onChange={handleBioChange} />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Contact Information
        </Typography>
        <ContactInfoNoticeInline value={value} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="Phone Number"
          value={value?.phoneNumber || ''}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PhoneIcon style={{ fontSize: '1.2rem' }} />
              </InputAdornment>
            ),
          }}
          onChange={(e) => onInputChange('phoneNumber', e.target.value)}
          size="small"
          error={Boolean((value?.phoneNumber || '').trim()) && !isValidNorthAmericanPhoneNumber(value?.phoneNumber || '')}
          helperText={
            Boolean((value?.phoneNumber || '').trim()) && !isValidNorthAmericanPhoneNumber(value?.phoneNumber || '')
              ? 'Enter a valid North American phone number (e.g., 604-555-1234 or +1 604 555 1234)'
              : ''
          }
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="WeChat ID"
          value={value?.weChatId || ''}
          onChange={(e) => onInputChange('weChatId', e.target.value)}
          size="small"
          error={Boolean((value?.weChatId || '').trim()) && !isValidWeChatId(value?.weChatId || '')}
          helperText={Boolean((value?.weChatId || '').trim()) && !isValidWeChatId(value?.weChatId || '') ? 'WeChat ID: 6-20 chars, start with letter, letters/digits/_/- only' : ''}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IoLogoWechat style={{ fontSize: '1.2rem' }} />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      {/* Sports */}
      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Sports
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {Object.values(SPORTS_OPTIONS).map((type) => (
            <Button
              key={type.value}
              startIcon={type.icon}
              variant={value?.sports?.includes(type.value) ? 'contained' : 'outlined'}
              size="small"
              onClick={() => {
                const newSports = value?.sports || []
                if (newSports.includes(type.value)) {
                  onInputChange(
                    'sports',
                    newSports.filter((s) => s !== type.value),
                  )
                } else {
                  onInputChange('sports', [...newSports, type.value])
                }
              }}
              sx={{
                borderRadius: 8,
                textTransform: 'none',
                px: 1,
                py: 0.5,
                fontSize: '0.8125rem',
              }}
            >
              {type.label}
            </Button>
          ))}
        </Stack>
      </Grid>
      {/* Certificates for instructors */}
      {value?.roles?.includes('INSTRUCTOR') && (
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ pb: 1 }}>
            Instructor Certificates
          </Typography>
          <Autocomplete
            multiple
            size="large"
            options={CERTIFICATIONS}
            value={value?.instructorCertificates || []}
            onChange={(event, newValue) => {
              onInputChange('instructorCertificates', newValue)
            }}
            renderInput={(params) => <TextField {...params} label="Certificates" />}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index })
                return <Chip key={key} label={option} size="small" {...tagProps} />
              })
            }
          />
        </Grid>
      )}

      {/* Family Members */}
      <Grid size={{ xs: 12 }}>
        <Divider sx={{ mb: 1 }} />
        <FamilyMemberForm familyMembers={value?.familyMembers || []} onChange={onFamilyMembersChange} userFirstName={value?.firstName || ''} userLastName={value?.lastName || ''} />
      </Grid>

      {/* Emergency Contact */}
      <Grid size={{ xs: 12 }}>
        <Divider sx={{ mt: 1 }} />
        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
          Emergency Contact
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField fullWidth label="Name" value={value?.emergencyContact?.name || ''} onChange={(e) => onEmergencyContactChange('name', e.target.value)} size="small" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Phone"
              value={value?.emergencyContact?.phone || ''}
              onChange={(e) => onEmergencyContactChange('phone', e.target.value)}
              size="small"
              error={Boolean((value?.emergencyContact?.phone || '').trim()) && !isValidNorthAmericanPhoneNumber(value?.emergencyContact?.phone || '')}
              helperText={
                Boolean((value?.emergencyContact?.phone || '').trim()) && !isValidNorthAmericanPhoneNumber(value?.emergencyContact?.phone || '')
                  ? 'Enter a valid North American phone number (e.g., 604-555-1234 or +1 604 555 1234)'
                  : ''
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Relationship"
              value={value?.emergencyContact?.relationship || ''}
              onChange={(e) => onEmergencyContactChange('relationship', e.target.value)}
              size="small"
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

UserProfileForm.propTypes = {
  value: PropTypes.object,
  onInputChange: PropTypes.func.isRequired,
  onEmergencyContactChange: PropTypes.func.isRequired,
  onFamilyMembersChange: PropTypes.func.isRequired,
}

ContactInfoNoticeInline.propTypes = {
  value: PropTypes.object,
}
